import * as React from 'react';
import { Button, Table } from 'react-bootstrap';
import Moment from 'react-moment';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/internal/operators';
import BusPositionService from '../../api/stadtbus/bus_position_service';
import { BusPositionSource, IBusPosition } from '../../interfaces/stadtbus/bus_position';
import { lazyInject } from '../../inversify';

interface IGpsDebugComponentProps {
  cleanedUp: boolean;
}

interface IGpsDebugComponentState {
  positionHistory: IBusPosition[];
  preferredSourceIndex: number;
}

class GpsDebugComponent extends React.Component<IGpsDebugComponentProps, IGpsDebugComponentState> {
  private static MAX_HISTORY: number = 150;

  @lazyInject(BusPositionService)
  private readonly busPositionService: BusPositionService;

  private readonly preferredSources = [undefined, BusPositionSource.gps, BusPositionSource.stwbs, BusPositionSource.ura];

  // emits an event if the component is unmounted
  private readonly onUnmount: Subject<boolean> = new Subject<boolean>();

  constructor(props: IGpsDebugComponentProps) {
    super(props);

    this.state = {
      positionHistory: [],
      preferredSourceIndex: 1,
    };

    this.handleNextPreferredSource = this.handleNextPreferredSource.bind(this);
  }

  public componentDidMount() {
    if (this.props.cleanedUp) {
      this.selectPreferredSource(this.state.preferredSourceIndex);
    } else {
      this.busPositionService.onBusPosition().pipe(
        takeUntil(this.onUnmount),
        filter(p => p.source === BusPositionSource.gps),
      ).subscribe(p => {
        let positionHistory = [...this.state.positionHistory];

        if (this.props.cleanedUp) {
          // Only show the latest event per vehicle

          const positionIndex = positionHistory.findIndex(q => q.id === p.id);
          if (positionIndex >= 0) {
            positionHistory[positionIndex] = p;
          } else {
            positionHistory.push(p);
            positionHistory.sort((a, b) => a.id.localeCompare(b.id));
          }

        } else {
          // Show all events

          positionHistory.push(p)
        }

        if (positionHistory.length > GpsDebugComponent.MAX_HISTORY) {
          positionHistory = positionHistory
            .slice(positionHistory.length - GpsDebugComponent.MAX_HISTORY, positionHistory.length);
        }

        this.setState({
          positionHistory,
        });
      });
    }
  }

  public componentWillUpdate(nextProps: IGpsDebugComponentProps, nextState: IGpsDebugComponentState) {
    if (nextProps.cleanedUp && this.state.preferredSourceIndex !== nextState.preferredSourceIndex) {
      this.selectPreferredSource(nextState.preferredSourceIndex);
    }
  }

  public componentWillUnmount() {
    this.onUnmount.next(true);
  }

  public render() {
    const preferredSource = this.preferredSources[this.state.preferredSourceIndex];
    const extraButton = this.props.cleanedUp && (
      <Button
        onClick={this.handleNextPreferredSource}>{preferredSource ? `Preferred source: ${preferredSource}` : `No preferred source`}</Button>
    );

    return (
      <div>
        <Button onClick={() => this.setState({positionHistory: []})}>Clear list</Button>
        {extraButton}
        <Table striped={true} bordered={true} condensed={true} hover={true}>
          <thead>
          <tr>
            <th>Timestamp</th>
            <th>Age</th>
            <th>Bus ID</th>
            <th>Position</th>
            <th>Source</th>
            <th>Line</th>
          </tr>
          </thead>
          <tbody>
          {this.state.positionHistory
            .map(p => (
              <tr key={`${p.id}-${p.timestamp}`}>
                <td><Moment date={p.timestamp} format={'DD.MM.YYYY HH:mm:ss'}/></td>
                <td><Moment date={p.timestamp} fromNow={true} interval={1000}/></td>
                <td>{p.id}</td>
                <td><a href={`${process.env.PUBLIC_URL}/stadtbus/karte/${p.position.latitude}/${p.position.longitude}`}
                       target="_blank">
                  {p.position.latitude}°N {p.position.longitude}°E
                </a></td>
                <td>{p.source}</td>
                <td>{p.line}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    );
  }

  private handleNextPreferredSource() {
    this.setState({
      preferredSourceIndex: (this.state.preferredSourceIndex + 1) % this.preferredSources.length,
    });
  }

  private selectPreferredSource(preferredSourceIndex: number) {
    const preferredSource = this.preferredSources[preferredSourceIndex];

    this.onUnmount.next(false);

    this.busPositionService.onBusPositions(preferredSource)
      .pipe(takeUntil(this.onUnmount))
      .subscribe(positions => {
        positions.sort((a, b) => a.id.localeCompare(b.id));

        this.setState({
          positionHistory: positions,
        });
      });
  }

}

export default GpsDebugComponent;



// WEBPACK FOOTER //
// ./src/components/debug/gps_debug_component.tsx